import { useEffect, useState } from 'react';

/**
 * Whether an element is on show: a dialog kept mounted and hidden (for an
 * exit animation, say) is not open, however it was hidden: `hidden` or
 * `inert`, `display: none` or `visibility: hidden`, on it or an ancestor.
 * Not opacity: modals fade in from 0 by animation, which no observer hears,
 * so a check during the fade would call an opening dialog closed. Dialogs inside a shadow root are not seen at all: none of
 * the app's are, and a document query cannot reach them.
 */
function showing(element: Element): boolean {
  if (element.closest('[hidden], [inert]')) return false;
  // visibilityProperty, and checkVisibilityCSS, its older name, for an engine that knows only that.
  if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true });
  // Without checkVisibility: display is inherited through the layout, so walk
  // the ancestors for it; visibility is inherited, so the element's own says.
  const view = element.ownerDocument.defaultView;
  if (!view) return true;
  if (view.getComputedStyle(element).visibility === 'hidden') return false;
  for (let at: Element | null = element; at; at = at.parentElement) {
    if (view.getComputedStyle(at).display === 'none') return false;
  }
  return true;
}

/** Whether a modal dialog is open (TOADO-971): a tour's own box is not modal, so it never counts. */
export function modalOpen(): boolean {
  const labelled = document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]');
  if ([...labelled].some(showing)) return true;
  // A native dialog opened with showModal() says so only through :modal
  // (one opened with show() is not modal); an engine without it has none.
  return [...document.querySelectorAll('dialog[open]')].some((dialog) => {
    try { return dialog.matches(':modal') && showing(dialog); } catch { return false; }
  });
}

/**
 * The modal on top (TOADO-988): the last open one in the document, since
 * each is portalled to the end of the body as it opens, so a window opened
 * over another comes after it. Null when none is open.
 */
export function topmostModal(): Element | null {
  const open = [...document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"], dialog[open]')]
    .filter((dialog) => {
      if (dialog.tagName === 'DIALOG' && !dialog.hasAttribute('aria-modal')) {
        try { if (!dialog.matches(':modal')) return false; } catch { return false; }
      }
      return showing(dialog);
    });
  return open.at(-1) ?? null;
}

/**
 * The same, kept current while mounted: a running tour hides while a modal
 * is open. Every attribute change is heard, since a dialog can be shown or
 * hidden by a class, a style or `hidden` as well as by `open`; the check
 * runs once a frame at most, and only while `enabled`: the tours watch for
 * modals only while one is showing or a launch is on the page.
 */
export function useModalOpen(enabled = true): boolean {
  const [open, setOpen] = useState(() => enabled && modalOpen());
  useEffect(() => {
    if (!enabled) {
      setOpen(false);
      return;
    }
    let frame = 0;
    const check = () => {
      frame = 0;
      setOpen(modalOpen());
    };
    const soon = () => { if (!frame) frame = window.requestAnimationFrame(check); };
    const observer = new MutationObserver(soon);
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
    check();
    return () => {
      observer.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [enabled]);
  return open;
}
